/**
 * @fileoverview Theme Toggle Hook
 * 
 * @description
 * Hook para gerenciar tema dark/light com persistência em localStorage.
 * Permite toggle entre Material Design 3 dark e light modes.
 * 
 * @version 3.0 - MD3 Dark/Light Toggle
 */

import { useState, useEffect, useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import { darkTheme, lightTheme } from '../theme';

type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'valuation-theme-mode';

export const useThemeMode = () => {
  // Inicia com dark theme (padrão MD3)
  const [mode, setMode] = useState<ThemeMode>('dark');

  // Carrega preferência salva do localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null;
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      setMode(savedMode);
    }
  }, []);

  // Salva preferência no localStorage quando muda
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  // Toggle entre light e dark
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Retorna o tema MUI apropriado
  const theme: Theme = useMemo(() => {
    return mode === 'light' ? lightTheme : darkTheme;
  }, [mode]);

  return {
    mode,
    theme,
    toggleTheme,
    isDark: mode === 'dark',
    isLight: mode === 'light',
  };
};
